import * as React from "react";
import "../../assets/crudStyles.css";
import "./styles.css";
import { TextInputCounter } from "../../components/TextInputCounter/TextInputCounter";
import {
  required,
  ImageInput,
  Toolbar,
  SaveButton,
  useNotify,
  ImageField,
  useRecordContext,
  useEditController,
  maxLength,
  TabbedForm,
  FormTab,
} from "react-admin";
import useFileUpload from "../../hooks/useFileUpload";
import { ResourceEditor } from "../../components/ResourceEditor/ResourceEditor";

import { CreateScreen as GroupEventPageSectionsCreate } from "../../components/group-event-page-sections/create";
import { EditScreen as GroupEventsPageSectionsEdit } from "../../components/group-event-page-sections/edit";

import { CreateScreen as NavigationGroupsCardCreate } from "../../components/navigation-groups-cards/create";
import { EditScreen as NavigationGroupsCardEdit } from "../../components/navigation-groups-cards/edit";
import { RaEditWrapper } from "../../components/RaEditWrapper";

interface IEdit {
  [key: string]: any;
}

const EditToolbar = ({ files, ...props }: any) => {
  return (
    <Toolbar {...props}>
      <SaveButton
        transform={({ ...data }: IEdit) => {
          const mappedRequest: any = {
            ...data,
            heroVideoUploadId: null,
          };

          const resourceFilesReq = {
            heroUploadId: files.heroUpload ?? data.heroUpload.id,
          };

          // Clean up fields.
          delete mappedRequest.topSectionImageUpload;
          delete mappedRequest.heroUpload;
          delete mappedRequest.order;
          delete mappedRequest.id;
          delete mappedRequest.enabled;

          return {
            ...mappedRequest,
            ...resourceFilesReq,
          };
        }}
        type="button"
      />
    </Toolbar>
  );
};

export const EditScreen = (props: any) => {
  const notify = useNotify();

  // Upload Files
  const [resourceFiles, setResourceFiles] = React.useState<{
    topSectionImageUpload: null | any;
    heroUpload: null | any;
  }>({
    topSectionImageUpload: null,
    heroUpload: null,
  });

  const [resourceImgs, setResourceImgs] = React.useState<{
    [key: string]: any;
  }>({});

  const renderImgPreview = (name: string) => {
    if (!resourceImgs[name]) {
      return <ImageField source="uri" title="info.original_filename" />;
    }

    return (
      <FileFieldWithContext
        placeholder={
          <div className="MuiBox-root css-cb34tx-RaImageField-root">
            <img
              src={resourceImgs[name].uri}
              title={resourceImgs[name].title}
              width={200}
              height={200}
              className="RaImageField-image"
            />
          </div>
        }
      >
        <ImageField source="uri" title="title" />
      </FileFieldWithContext>
    );
  };

  // Used to track the uploaded files and show in progress or error.
  const [uploadedFileNames, setUploadFileNames] = React.useState<{
    [key: string]: string | boolean;
  }>({});

  const { uploadFile } = useFileUpload();
  const handlePrefileUpload = async (name: string, files: File[]) => {
    const fileName = files[0].name;
    notify(`Uploading file... ${fileName}`);

    setUploadFileNames({ ...uploadedFileNames, [fileName]: "Uploading..." });

    const asset = await uploadFile(files[0], name);
    if (asset !== undefined) {
      setResourceImgs({
        ...resourceImgs,
        [name]: {
          title: asset.info.original_filename,
          uri: asset.uri,
          uploading: false,
        },
      });

      const newValue = asset.id;
      setResourceFiles({ ...resourceFiles, [name]: newValue });
      notify(`Upload completed! File: ${fileName}`);
      setUploadFileNames({ ...uploadedFileNames, [fileName]: true });
    } else {
      notify(`Error uploading file! ${fileName}`);
      setUploadFileNames({
        ...uploadedFileNames,
        [fileName]: "Error uploading file, remove and try again.",
      });
    }
  };

  const FileFieldWithContext = (props: any) => {
    const { title, uri } = useRecordContext();

    if (uri) {
      return props.children;
    }

    if (!Object.hasOwn(uploadedFileNames, title)) {
      return null;
    }

    if (uploadedFileNames[title] === true) {
      return props.placeholder;
    } else {
      return <p>{uploadedFileNames[title]}</p>;
    }
  };

  // Record handler.
  const { record } = useEditController(props);

  if (record) {
    record.enabled = record.publishedAt !== null ? true : false;
  }

  return (
    <RaEditWrapper mutationMode="pessimistic">
      <TabbedForm
        toolbar={<EditToolbar files={resourceFiles} record={record} />}
      >
        <FormTab label="Hero">
          <TextInputCounter
            formClassName="form-text-input-field"
            source="heroHeading"
            fullWidth
            multiline
            validate={[required(), maxLength(100)]}
            limit={100}
          />

          <div className="resource-icons-grid">
            <ImageInput
              source="heroUpload"
              label="Image (.png, .jpg, .jpeg)"
              validate={[required()]}
              accept={"image/png,image/jpg,image/jpeg"}
              options={{
                onDropAccepted: handlePrefileUpload.bind(null, "heroUpload"),
              }}
            >
              {renderImgPreview("heroUpload")}
            </ImageInput>
          </div>
        </FormTab>

        <FormTab label="Form">
          <div className="form-text-input-row">
            <TextInputCounter
              formClassName="form-text-input-field"
              source="groupContactFormId"
              validate={[required()]}
            />
          </div>
        </FormTab>

        <FormTab label="Sections">
          <ResourceEditor
            resourceId={"group-event-page-sections"}
            createNewLabel="Add New Section"
            updatedOrderMsg="Section order updated."
            updatingOrderMsg="Updating section order..."
            heading="Sections"
            renderName={(resource: any) => resource.header}
            CreateScreen={GroupEventPageSectionsCreate}
            EditScreen={GroupEventsPageSectionsEdit}
            hasOrder={true}
            hasEnabled={true}
          />
        </FormTab>

        <FormTab label="Navigations">
          <ResourceEditor
            resourceId={"navigation-groups-cards"}
            createNewLabel="Add New Card"
            updatedOrderMsg="Card order updated."
            updatingOrderMsg="Updating card order..."
            heading="Cards"
            CreateScreen={NavigationGroupsCardCreate}
            EditScreen={NavigationGroupsCardEdit}
            hasOrder={true}
            hasEnabled={true}
          />
        </FormTab>
      </TabbedForm>
    </RaEditWrapper>
  );
};
